import { inject, Injectable } from '@angular/core';
import { RightEnum } from '@core/models/rights.model';
import { Constants } from '@core/app-constants';
import {
  ApiException,
  DocumentTypeEnumRequestDto,
  ExportOrderItemTypeEnumRequestDto,
  ExportOrderPropertyEnumRequestDto,
  FileResponse,
  OperatorTypeEnumRequestDto,
  OrderByDirectionEnum,
  OrderItemRowTypeEnumResponseDto,
  OrderItemStatusEnumResponseDto,
} from '@core/services/apiclient';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as fromOrders from '@root-store/orders-store/index';
import { OffcanvasComponent } from '@shared/components';
import { forkJoin, Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { TreeListAction } from '../models/dx-tree-list.model';
import {
  isDuplicable,
  isSubscriptionGroup,
  OrderDisplayType,
  OrdersActionLevel,
  OrdersActionType,
  TreeListRowData,
} from '../models/orders.model';
import { AlertService } from '@core/services/alert.service';
import { Utils } from '@shared/utils/utils';
import { OrdersService } from './orders.service';
import { OrdersProcessFacade } from './orders-process.facade';
import { OrdersStatusService } from './orders-status.service';
import { OrdersSubscriptionGroupService } from './orders-subscription-group.service';
import { OrdersTreeDatasourceService } from './orders-tree-datasource.service';

@Injectable()
export class OrdersActionsService {
  private readonly store = inject<Store<fromOrders.State>>(Store);
  private readonly translate = inject(TranslateService);
  private readonly statusService = inject(OrdersStatusService);
  private readonly processFacade = inject(OrdersProcessFacade);
  private readonly subscriptionGroupService = inject(OrdersSubscriptionGroupService);
  private readonly datasourceService = inject(OrdersTreeDatasourceService);
  private readonly orderService = inject(OrdersService);
  private readonly utils = inject(Utils);
  private readonly alertService = inject(AlertService);

  getOrdersActions(offcanvas: OffcanvasComponent): TreeListAction[] {
    return [
      this.createExportSelectionAction(),
      this.createPrintAction(),
      this.createDuplicateAction(),
      this.createPayAction(),
      this.createRefundAction(),
      this.createCancelReservedAction(),
      this.createDocumentAction(),
      this.createDetailsAction(offcanvas),
    ];
  }

  private formatContextLabel(baseLabel: string, eligibleCount: number, totalCount: number): string {
    if (totalCount > 0 && eligibleCount !== totalCount) {
      return `${baseLabel} ${eligibleCount}/${totalCount}`;
    }
    return baseLabel;
  }

  private getSubscriptionTotalCountExcludingCanceled(rowData: TreeListRowData): number {
    const totalCount = rowData?.orderItemCount ?? 0;
    const canceledCount = rowData?.canceledOrderRowsCount ?? 0;
    return Math.max(0, totalCount - canceledCount);
  }

  private normalizeSelection(selection: TreeListRowData[]): TreeListRowData[] {
    return this.subscriptionGroupService.normalizeSelection(selection);
  }

  private getPayableItems(selection: TreeListRowData[]): Observable<TreeListRowData[]> {
    const normalized = this.normalizeSelection(selection);

    const regularItems: TreeListRowData[] = [];
    const entireGroups: TreeListRowData[] = [];
    const partialGroups: TreeListRowData[] = [];

    normalized.forEach((row) => {
      if (this.subscriptionGroupService.isEntirePaymentGroup(row)) entireGroups.push(row);
      else if (this.subscriptionGroupService.isPartialPaymentGroup(row)) partialGroups.push(row);
      else if (this.subscriptionGroupService.isRegularReserved(row)) regularItems.push(row);
    });

    if (entireGroups.length === 0 && partialGroups.length === 0) {
      return of(regularItems);
    }

    const entireGroups$ = entireGroups.map((group) =>
      this.subscriptionGroupService.calculateReservedAmountForSubscriptionGroup(group),
    );

    const partialGroups$ =
      partialGroups.length > 0
        ? this.subscriptionGroupService.prepareSelectionWithSubscriptionGroups(
            partialGroups,
            (status) => status === OrderItemStatusEnumResponseDto.Reserved,
            () => true,
          )
        : of([] as TreeListRowData[]);

    return forkJoin([...entireGroups$, partialGroups$]).pipe(
      map((results) => {
        const updatedEntireGroups = results.slice(0, entireGroups.length) as TreeListRowData[];
        const updatedPartialGroups = results.slice(entireGroups.length).flat() as TreeListRowData[];
        return [...regularItems, ...updatedEntireGroups, ...updatedPartialGroups];
      }),
    );
  }

  private getPrintOrderSelection(selection: TreeListRowData[]): {
    orderItems: TreeListRowData[];
    orderSubscriptions: TreeListRowData[];
  } {
    const allRows = this.datasourceService.currentData();
    const { selectedSubscriptionGroups, selectedOrderItems } =
      this.subscriptionGroupService.resolveSubscriptionSelection(
        selection,
        allRows,
        this.datasourceService.areLoadedChildrenComplete(),
      );

    return {
      orderSubscriptions: selectedSubscriptionGroups.filter(
        (row) => this.statusService.isPrintable(row) === true,
      ),
      orderItems: selectedOrderItems.filter((row) => this.statusService.isPrintable(row) === true),
    };
  }

  private getRefundableItems(selection: TreeListRowData[]): Observable<TreeListRowData[]> {
    const normalized = this.normalizeSelection(selection);

    const regularItems: TreeListRowData[] = [];
    const entireGroups: TreeListRowData[] = [];
    const partialGroups: TreeListRowData[] = [];

    normalized.forEach((row) => {
      if (this.subscriptionGroupService.isEntireRefundGroup(row)) entireGroups.push(row);
      else if (this.subscriptionGroupService.isPartialRefundGroup(row)) partialGroups.push(row);
      else if (this.subscriptionGroupService.isRegularRefundable(row)) regularItems.push(row);
    });

    if (entireGroups.length === 0 && partialGroups.length === 0) {
      return of(regularItems);
    }

    const entireGroups$ = entireGroups.map((group) =>
      this.subscriptionGroupService.calculateRefundAmountForSubscriptionGroup(group),
    );

    const partialGroups$ =
      partialGroups.length > 0
        ? this.subscriptionGroupService.prepareSelectionWithSubscriptionGroups(
            partialGroups,
            (status) =>
              status === OrderItemStatusEnumResponseDto.Paid ||
              status === OrderItemStatusEnumResponseDto.Edited,
            () => true,
          )
        : of([] as TreeListRowData[]);

    return forkJoin([...entireGroups$, partialGroups$]).pipe(
      map((results) => {
        const updatedEntireGroups = results.slice(0, entireGroups.length) as TreeListRowData[];
        const updatedPartialGroups = results.slice(entireGroups.length).flat() as TreeListRowData[];
        return [...regularItems, ...updatedEntireGroups, ...updatedPartialGroups];
      }),
    );
  }

  private getPayCounts(row: TreeListRowData): { eligible: number; total: number } {
    if (isSubscriptionGroup(row)) {
      return { eligible: row.reservedOrderRowsCount ?? 0, total: row.orderItemCount ?? 0 };
    }
    return { eligible: row?.statusGroup === OrderItemStatusEnumResponseDto.Reserved ? 1 : 0, total: 1 };
  }

  private getPrintCounts(row: TreeListRowData): { eligible: number; total: number } {
    if (isSubscriptionGroup(row)) {
      return { eligible: row.paidOrderRowsCount ?? 0, total: row.orderItemCount ?? 0 };
    }
    return { eligible: this.statusService.isPrintable(row) === true ? 1 : 0, total: 1 };
  }

  private getDuplicateCounts(row: TreeListRowData): { eligible: number; total: number } {
    if (isSubscriptionGroup(row)) {
      const total = row.orderItemCount ?? 0;
      const eligible = isDuplicable(row) ? Math.min(row.editedOrderRowsCount ?? 0, total) : 0;
      return { eligible, total };
    }
    return { eligible: isDuplicable(row) ? 1 : 0, total: 1 };
  }

  private getRefundCounts(row: TreeListRowData): { eligible: number; total: number } {
    if (isSubscriptionGroup(row)) {
      const eligible = (row.paidOrderRowsCount ?? 0) + (row.editedOrderRowsCount ?? 0);
      return { eligible, total: row.orderItemCount ?? 0 };
    }
    return {
      eligible:
        row?.statusGroup === OrderItemStatusEnumResponseDto.Paid ||
        row?.statusGroup === OrderItemStatusEnumResponseDto.Edited
          ? 1
          : 0,
      total: 1,
    };
  }
  /**
   * Export the picked rows as CSV.
   *
   * Offered per row as well as over a selection: the action bar's own filter consults
   * `hidden` too, and its `allItemsIgnoreHidden` escape hatch re-applies it when exactly
   * one row is selected — which would hide the export in the one case it must work.
   */
  private createExportSelectionAction(): TreeListAction {
    return {
      class: 'tick-btn-transparent',
      label: this.translate.instant('orders.export.export'),
      dataCy: 'export-selection',
      command: (rowData: TreeListRowData) => {
        this.exportSelectedOrderItems([rowData]);
      },
      allItemsCommand: (selection: TreeListRowData[]) => {
        this.exportSelectedOrderItems(selection);
      },
      // Declared even though it never hides: the shared row template calls `hidden`
      // per row, so an action that omits it is what stops the list rendering.
      hidden: () => false,
    };
  }

  /**
   * Resolve a selection into the `ID_OrderRow`s the export criterion accepts.
   *
   * A subscription-group row carries an `ID_PackageRow`, not an `ID_OrderRow`, so it
   * cannot travel as an `OrderItemNumber`: the criterion joins on `ID_OrderRow`, and a
   * package id resolves against a different table, yielding folders of unrelated orders.
   * It is therefore expanded into its own folders. This deliberately does NOT reuse
   * `updateSelectedFoldersIds`, which serves another purpose and diverges twice: it keeps
   * nested group rows, and it falls back to `[row.id]` when nothing is loaded — i.e. it
   * puts the `ID_PackageRow` into `selectedFoldersIds`. Harmless there (that signal is only
   * ever read for its `.length`, by `repaintSelectAllCheckbox`), fatal here. Do not align
   * the two.
   *
   * Dropping the group row instead is **not** an option, and this is the non-obvious
   * part: `OrdersSelectionService.handleSelectedRow` normalises the selection ONTO the
   * parent — it deletes the children from it and keeps the group row alone. So a selected
   * formula is represented by that row and nothing else, whether or not its children were
   * ever loaded.
   *
   * The children sit in the datasource only once the group has been expanded (they are
   * fetched per parent by `loadChildren`, which appends them to `currentData`). A group
   * that was never expanded therefore resolves to nothing, and the caller reports it
   * rather than shipping a file quietly missing those folders.
   */
  private resolveSelectionFolderIds(selection: TreeListRowData[]): {
    ids: number[];
    unresolvedGroups: number;
  } {
    const rows = selection ?? [];
    const currentData = this.datasourceService.currentData();
    const foldersOf = (groupId: number): TreeListRowData[] =>
      currentData.filter((row) => row?.parentId === groupId && !row?.hasItems);

    const ids = rows.flatMap((row) => {
      if (!row?.hasItems) {
        return row?.id != null ? [row.id] : [];
      }
      return foldersOf(row.id)
        .map((folder) => folder?.id)
        .filter((id): id is number => id != null);
    });

    return {
      // De-duplicated: the selection normalisation keeps a parent and its children
      // mutually exclusive, so this is insurance rather than a known case — but a
      // repeated id would make the server dedupe on our behalf.
      ids: [...new Set(ids)],
      unresolvedGroups: rows.filter((row) => row?.hasItems && foldersOf(row.id).length === 0)
        .length,
    };
  }

  /**
   * Export exactly the folders passed in — a picked selection, not the current filter.
   *
   * `OrderItemNumber` is the only property a selection can travel on: `OrderNumber`
   * accepts `Equal` alone server-side. `In` is encoded as a JSON-array string in a single
   * value, and the reader turns it into a table-valued parameter.
   */
  exportSelectedOrderItems(selection: TreeListRowData[]): void {
    const { ids, unresolvedGroups } = this.resolveSelectionFolderIds(selection);
    if (unresolvedGroups > 0) {
      this.alertService.showCustom('orders.export.subscriptionFoldersNotLoaded', 'warning');
    }
    if (!ids.length) {
      return;
    }
    // The same ceiling the by-filter export enforces, restated because that one is a
    // disabled button reading a server total, which a selection never passes through.
    if (ids.length > Constants.MaxExportCount) {
      this.alertService.showCustom(
        'orders.export.maximumNumberOfResultsToExport',
        'warning',
        undefined,
        undefined,
        { maxExport: Constants.MaxExportCount },
      );
      return;
    }
    this.orderService
      .exportOrderItems({
        criterias: [
          {
            property: ExportOrderPropertyEnumRequestDto.OrderItemNumber,
            condition: OperatorTypeEnumRequestDto.In,
            value: `[${ids.join(',')}]`,
          },
        ],
        includes: [ExportOrderItemTypeEnumRequestDto.Session, ExportOrderItemTypeEnumRequestDto.Product],
        orderBy: {
          direction: OrderByDirectionEnum.Desc,
          property: ExportOrderPropertyEnumRequestDto.BookingDate,
        },
        isDefault: false,
      })
      .subscribe({
        next: (value: FileResponse) => {
          this.utils.saveToFileSystem(value.data, value?.fileName, value?.headers?.['content-type']);
          this.alertService.showCustom('orders.export.exportIsSuccessfullyDownloaded', 'success');
        },
        error: (error: unknown) => {
          const fallback =
            error instanceof ApiException && error.status === 403
              ? 'orders.export.notAuthorizedToExport'
              : 'orders.export.problemOccurredWhileExporting';
          this.alertService.showApiError(error, fallback);
        },
      });
  }

  getOrdersFixedActions(offcanvas: OffcanvasComponent): TreeListAction[] {
    return [
      {
        class: 'tick-btn-default',
        wrapperClass: 'orders-list-details-btn',
        tooltipValue: 'orders.tooltip.accessToOrderDetails',
        icon: 'edit_note',
        dataCy: 'edit',
        allItemsCommand: (selection: TreeListRowData[]) => {
          if (selection?.length === 1) {
            this.store.dispatch(
              fromOrders.actions.loadOrderItems({
                orderId: selection[0]?.orderId,
                orderItemId: selection[0]?.id,
              }),
            );
            offcanvas.open();
          }
        },
        command: (rowData: TreeListRowData) => {
          this.store.dispatch(
            fromOrders.actions.loadOrderItems({ orderId: rowData?.orderId, orderItemId: rowData?.id }),
          );
          offcanvas.open();
        },
        hidden: () => false,
        allItemsHidden: (selection: TreeListRowData[]) => selection?.length > 1,
      },
    ];
  }

  private isEditHidden(rowData: TreeListRowData): boolean {
    if (!rowData) return true;

    if (isSubscriptionGroup(rowData)) {
      return (rowData.paidOrderRowsCount ?? 0) === 0;
    }

    if (rowData.orderItemRowType === OrderItemRowTypeEnumResponseDto.Product) {
      return !this.statusService.isProductPrintable(rowData);
    }

    return rowData.statusGroup !== OrderItemStatusEnumResponseDto.Paid;
  }

  private isPayHidden(rowData: TreeListRowData): boolean {
    if (!rowData) return true;
    if (isSubscriptionGroup(rowData)) {
      return (rowData.reservedOrderRowsCount ?? 0) === 0;
    }
    return rowData.statusGroup !== OrderItemStatusEnumResponseDto.Reserved;
  }

  private isRepaymentHidden(rowData: TreeListRowData): boolean {
    if (!rowData) return true;
    return (
      rowData.statusGroup === OrderItemStatusEnumResponseDto.Canceled ||
      rowData.statusGroup === OrderItemStatusEnumResponseDto.Reserved
    );
  }

  private isReleaseHidden(rowData: TreeListRowData): boolean {
    if (!rowData) return true;
    if (isSubscriptionGroup(rowData)) {
      return rowData.reservedOrderRowsCount === 0;
    }
    return rowData.statusGroup !== OrderItemStatusEnumResponseDto.Reserved;
  }

  private getReservedCancellableRows(selection: TreeListRowData[]): TreeListRowData[] {
    const normalized = this.normalizeSelection(selection);
    return normalized.filter((row) => {
      if (isSubscriptionGroup(row)) {
        return (row.reservedOrderRowsCount ?? 0) > 0;
      }
      return row.statusGroup === OrderItemStatusEnumResponseDto.Reserved;
    });
  }

  private isDocumentHidden(rowData: TreeListRowData): boolean {
    if (!rowData) return true;
    if (isSubscriptionGroup(rowData)) {
      return true;
    }
    return rowData.statusGroup === OrderItemStatusEnumResponseDto.Canceled;
  }

  private createPrintAction(): TreeListAction {
    const baseLabel = this.translate.instant('shared.actions.print');

    return {
      class: 'tick-btn-primary',
      label: baseLabel,
      selectionLabel: (selection: TreeListRowData[]) => {
        const normalized = this.normalizeSelection(selection);
        const totals = normalized.reduce(
          (acc, row) => {
            const counts = this.getPrintCounts(row);
            return { eligible: acc.eligible + counts.eligible, total: acc.total + counts.total };
          },
          { eligible: 0, total: 0 },
        );
        if (totals.total > 0 && totals.eligible !== totals.total) {
          return `${baseLabel} ${totals.eligible}/${totals.total}`;
        }
        return baseLabel;
      },
      contextLabel: (rowData: TreeListRowData) => {
        if (isSubscriptionGroup(rowData)) {
          const totalCount = this.getSubscriptionTotalCountExcludingCanceled(rowData);
          const paidCount = rowData.paidOrderRowsCount ?? 0;
          return this.formatContextLabel(baseLabel, paidCount, totalCount);
        }
        return baseLabel;
      },
      rights: [RightEnum.OrderPrint],
      dataCy: 'print',
      command: (rowData: TreeListRowData) => {
        this.dispatchPrintAction([rowData]);
      },
      allItemsCommand: (selection: TreeListRowData[]) => {
        this.dispatchPrintAction(selection);
      },
      hidden: (rowData: TreeListRowData) => this.isEditHidden(rowData),
      allItemsHidden: (selection: TreeListRowData[]) => {
        if (!selection?.length) {
          return true;
        }
        const normalized = this.normalizeSelection(selection);
        return !normalized.some((row) => this.statusService.isPrintable(row) === true);
      },
      allItemsIgnoreHidden: true,
    };
  }

  private createDuplicateAction(): TreeListAction {
    const baseLabel = this.translate.instant('shared.actions.duplicate');

    return {
      class: 'tick-btn-primary',
      label: baseLabel,
      selectionLabel: (selection: TreeListRowData[]) => {
        const normalized = this.normalizeSelection(selection);
        const totals = normalized.reduce(
          (acc, row) => {
            const counts = this.getDuplicateCounts(row);
            return { eligible: acc.eligible + counts.eligible, total: acc.total + counts.total };
          },
          { eligible: 0, total: 0 },
        );
        if (totals.total > 0 && totals.eligible !== totals.total) {
          return `${baseLabel} ${totals.eligible}/${totals.total}`;
        }
        return baseLabel;
      },
      contextLabel: (rowData: TreeListRowData) => {
        if (isSubscriptionGroup(rowData)) {
          const totalCount = this.getSubscriptionTotalCountExcludingCanceled(rowData);
          const editedCount = Math.min(rowData.editedOrderRowsCount ?? 0, totalCount);
          return this.formatContextLabel(baseLabel, editedCount, totalCount);
        }
        return baseLabel;
      },
      rights: [RightEnum.Duplicate],
      dataCy: 'duplicate',
      command: (rowData: TreeListRowData) => {
        this.dispatchDuplicateAction([rowData]);
      },
      allItemsCommand: (selection: TreeListRowData[]) => {
        this.dispatchDuplicateAction(selection);
      },
      hidden: (rowData: TreeListRowData) => !isDuplicable(rowData),
      allItemsHidden: (selection: TreeListRowData[]) => {
        if (!selection?.length) {
          return true;
        }
        const normalized = this.normalizeSelection(selection);
        return !normalized.some((row) => isDuplicable(row));
      },
      allItemsIgnoreHidden: true,
    };
  }

  private createPayAction(): TreeListAction {
    const baseLabel = this.translate.instant('shared.actions.pay');

    return {
      class: 'tick-btn-primary',
      label: baseLabel,
      selectionLabel: (selection: TreeListRowData[]) => {
        const normalized = this.normalizeSelection(selection);
        const totals = normalized.reduce(
          (acc, row) => {
            const counts = this.getPayCounts(row);
            return { eligible: acc.eligible + counts.eligible, total: acc.total + counts.total };
          },
          { eligible: 0, total: 0 },
        );
        if (totals.total > 0 && totals.eligible !== totals.total) {
          return `${baseLabel} ${totals.eligible}/${totals.total}`;
        }
        return baseLabel;
      },
      contextLabel: (rowData: TreeListRowData) => {
        if (isSubscriptionGroup(rowData)) {
          const reservedCount = rowData.reservedOrderRowsCount ?? 0;
          const totalCount = this.getSubscriptionTotalCountExcludingCanceled(rowData);
          return this.formatContextLabel(baseLabel, reservedCount, totalCount);
        }
        return baseLabel;
      },
      rights: [RightEnum.Payment],
      dataCy: 'pay',
      command: (rowData: TreeListRowData) => {
        this.getPayableItems([rowData])
          .pipe(
            tap((payable) => {
              if (payable.length > 0) this.payOrderItems(payable);
            }),
          )
          .subscribe();
      },
      allItemsCommand: (selection: TreeListRowData[]) => {
        this.getPayableItems(selection)
          .pipe(
            tap((payable) => {
              if (payable.length > 0) this.payOrderItems(payable);
            }),
          )
          .subscribe();
      },
      hidden: (rowData: TreeListRowData) => {
        return this.isPayHidden(rowData);
      },
      allItemsHidden: (selection: TreeListRowData[]) => {
        const normalized = this.normalizeSelection(selection);
        return normalized.length === 0 || normalized.every((row) => this.isPayHidden(row));
      },
      allItemsIgnoreHidden: true,
    };
  }

  private createRefundAction(): TreeListAction {
    const baseLabel = this.translate.instant('shared.actions.refund');

    return {
      class: 'tick-btn-transparent',
      label: baseLabel,
      selectionLabel: (selection: TreeListRowData[]) => {
        const normalized = this.normalizeSelection(selection);
        const totals = normalized.reduce(
          (acc, row) => {
            const counts = this.getRefundCounts(row);
            return { eligible: acc.eligible + counts.eligible, total: acc.total + counts.total };
          },
          { eligible: 0, total: 0 },
        );
        if (totals.total > 0 && totals.eligible !== totals.total) {
          return `${baseLabel} ${totals.eligible}/${totals.total}`;
        }
        return baseLabel;
      },
      contextLabel: (rowData: TreeListRowData) => {
        if (isSubscriptionGroup(rowData)) {
          const eligible = (rowData.paidOrderRowsCount ?? 0) + (rowData.editedOrderRowsCount ?? 0);
          const totalCount = this.getSubscriptionTotalCountExcludingCanceled(rowData);
          return this.formatContextLabel(baseLabel, eligible, totalCount);
        }
        return baseLabel;
      },
      rights: [RightEnum.Repayment],
      dataCy: 'refund',
      command: (rowData: TreeListRowData) => {
        this.getRefundableItems([rowData])
          .pipe(
            tap((refundable) => {
              if (refundable.length > 0) this.refundOrderItems(refundable);
            }),
          )
          .subscribe();
      },
      allItemsCommand: (selection: TreeListRowData[]) => {
        this.getRefundableItems(selection)
          .pipe(
            tap((refundable) => {
              if (refundable.length > 0) this.refundOrderItems(refundable);
            }),
          )
          .subscribe();
      },
      hidden: (rowData: TreeListRowData) => {
        return this.isRepaymentHidden(rowData);
      },
      allItemsHidden: (selection: TreeListRowData[]) => {
        const normalized = this.normalizeSelection(selection);
        return normalized.length === 0 || normalized.every((row) => this.isRepaymentHidden(row));
      },
      allItemsIgnoreHidden: true,
    };
  }

  private createCancelReservedAction(): TreeListAction {
    const baseLabel = this.translate.instant('shared.actions.cancelReservation');

    return {
      class: 'tick-btn-transparent',
      label: baseLabel,
      selectionLabel: (selection: TreeListRowData[]) => {
        const normalized = this.normalizeSelection(selection);
        const totals = normalized.reduce(
          (acc, row) => {
            const counts = this.getPayCounts(row);
            return { eligible: acc.eligible + counts.eligible, total: acc.total + counts.total };
          },
          { eligible: 0, total: 0 },
        );
        if (totals.total > 0 && totals.eligible !== totals.total) {
          return `${baseLabel} ${totals.eligible}/${totals.total}`;
        }
        return baseLabel;
      },
      contextLabel: (rowData: TreeListRowData) => {
        if (isSubscriptionGroup(rowData)) {
          const reservedCount = rowData.reservedOrderRowsCount ?? 0;
          const totalCount = this.getSubscriptionTotalCountExcludingCanceled(rowData);
          return this.formatContextLabel(baseLabel, reservedCount, totalCount);
        }
        return baseLabel;
      },
      rights: [RightEnum.Release],
      dataCy: 'cancelReservedSeats',
      command: (rowData: TreeListRowData) => {
        const reserved = this.getReservedCancellableRows([rowData]);
        if (reserved.length > 0) {
          this.cancelReservedOrderItems(reserved);
        }
      },
      allItemsCommand: (selection: TreeListRowData[]) => {
        const reserved = this.getReservedCancellableRows(selection);
        if (reserved.length > 0) {
          this.cancelReservedOrderItems(reserved);
        }
      },
      hidden: (rowData: TreeListRowData) => {
        return this.isReleaseHidden(rowData);
      },
      allItemsHidden: (selection: TreeListRowData[]) => {
        const normalized = this.normalizeSelection(selection);
        return normalized.length === 0 || normalized.every((row) => this.isReleaseHidden(row));
      },
      allItemsIgnoreHidden: true,
    };
  }

  private createDocumentAction(): TreeListAction {
    return {
      class: 'tick-btn-transparent',
      label: this.translate.instant('orders.document'),
      dataCy: 'document',
      command: () => {},
      allItemsCommand: () => {},
      hidden: (rowData: TreeListRowData) => {
        return this.isDocumentHidden(rowData);
      },
      allItemsHidden: (selection: TreeListRowData[]) => {
        return (selection ?? []).some((rowData) => this.isDocumentHidden(rowData));
      },
      allItemsIgnoreHidden: true,
      multiple: true,
      groupItems: (rowData: TreeListRowData | TreeListRowData[]) =>
        this.getDocumentDownloadItems(Array.isArray(rowData) ? rowData : [rowData]),
    };
  }

  private createDetailsAction(offcanvas: OffcanvasComponent): TreeListAction {
    return {
      class: 'tick-btn-default',
      wrapperClass: 'orders-list-details-btn',
      icon: 'edit_note',
      tooltipValue: 'orders.tooltip.accessToOrderDetails',
      dataCy: 'edit',
      command: (rowData: TreeListRowData) => {
        this.store.dispatch(
          fromOrders.actions.loadOrderItems({ orderId: rowData?.orderId, orderItemId: rowData?.id }),
        );
        offcanvas.open();
      },
      hidden: () => false,
    };
  }

  getDocumentDownloadItems(selectedFolders: TreeListRowData[]): any[] {
    return [
      {
        label: this.translate.instant('orders.downloadDocument'),
        action: null,
        notListed: true,
      },
      {
        label: this.translate.instant('orders.paidInvoice'),
        action: () =>
          this.downloadDocument(
            DocumentTypeEnumRequestDto.Invoice,
            selectedFolders.map((a) => a.id),
          ),
        hide: !this.statusService.isPaid(selectedFolders),
      },
      {
        label: this.translate.instant('orders.proofOfPayment'),
        action: () =>
          this.downloadDocument(
            DocumentTypeEnumRequestDto.PaymentProof,
            selectedFolders.map((a) => a.id),
          ),
        hide: !this.statusService.isPaid(selectedFolders),
      },
      {
        label: this.translate.instant('orders.bookingCoupon'),
        action: () =>
          this.downloadDocument(
            DocumentTypeEnumRequestDto.BookingReceipt,
            selectedFolders.map((a) => a.id),
          ),
      },
    ];
  }

  getDisplayType(orderItems: TreeListRowData[]): OrderDisplayType {
    return orderItems?.length === 1
      ? OrderDisplayType.SingleFolderSelection
      : OrderDisplayType.MultipleFolderSelection;
  }

  private dispatchPrintAction(selection: TreeListRowData[]) {
    const printable = this.getPrintOrderSelection(selection);

    if (printable.orderItems.length === 0 && printable.orderSubscriptions.length === 0) {
      return;
    }

    this.store.dispatch(
      fromOrders.actions.setOrderActionPayload({
        orderActionType: OrdersActionType.Print,
        level: OrdersActionLevel.List,
        displayType: OrderDisplayType.MultipleSubscriptionSelection,
        items: printable.orderItems,
        orderSubscriptions: printable.orderSubscriptions,
      }),
    );
  }

  private getDuplicableOrderSelection(selection: TreeListRowData[]): {
    orderItems: TreeListRowData[];
    orderSubscriptions: TreeListRowData[];
  } {
    const allRows = this.datasourceService.currentData();
    const { selectedSubscriptionGroups, selectedOrderItems } =
      this.subscriptionGroupService.resolveSubscriptionSelection(
        selection,
        allRows,
        this.datasourceService.areLoadedChildrenComplete(),
      );

    return {
      orderSubscriptions: selectedSubscriptionGroups.filter((row) => isDuplicable(row)),
      orderItems: selectedOrderItems.filter((row) => isDuplicable(row)),
    };
  }

  private dispatchDuplicateAction(selection: TreeListRowData[]) {
    const duplicable = this.getDuplicableOrderSelection(selection);

    if (duplicable.orderItems.length === 0 && duplicable.orderSubscriptions.length === 0) {
      return;
    }

    this.store.dispatch(
      fromOrders.actions.setOrderActionPayload({
        orderActionType: OrdersActionType.Duplication,
        level: OrdersActionLevel.List,
        displayType: OrderDisplayType.MultipleSubscriptionSelection,
        items: duplicable.orderItems,
        orderSubscriptions: duplicable.orderSubscriptions,
      }),
    );
  }

  payOrderItems(orderItems: TreeListRowData[]): void {
    this.processFacade.payOrderItems(orderItems);
  }

  refundOrderItems(orderItems: TreeListRowData[]): void {
    this.processFacade.refundOrderItems(orderItems);
  }

  cancelReservedOrderItems(orderItems: TreeListRowData[]): void {
    this.processFacade.cancelReservedOrderItems(orderItems);
  }

  private downloadDocument(
    type: DocumentTypeEnumRequestDto,
    orderItemIds?: number[],
    orderId?: number,
  ): void {
    this.store.dispatch(
      fromOrders.actions.processOrderDocument({ request: { orderId, orderItemIds, type } }),
    );
  }
}
